import { Injectable } from '@angular/core';
import { from, Observable, of, TimeoutError } from 'rxjs';
import { HttpEvent, HttpEventType, HttpProgressEvent, HttpResponse} from '@angular/common/http';
import { Platform } from '@ionic/angular';
import { CurrentSettings, State, StateInterface } from '../../interfaces/biozoom-api/app-configuration';
import { SerialDispatcher } from '../../methods/serial-dispatcher';
import { ScannerStatus } from '../../interfaces/scanner-api/scanner-status';
import {catchError, delay, repeat, switchMap, timeout} from 'rxjs/operators';
import {parseStatus} from '../../methods/helpers';

const expectedKeys = [
  'host',
  'product',
  'serial',
  'sensor',
  'sensor_firmware',
  'sensor_temp',
  'ip',
  'battery_voltage',
  'usb_voltage',
  'usb_charging',
  'memory_free_64k_blocks',
  'memory_healthy',
  'wifi_firmware',
  'wifi_name',
  'wifi_strength',
  'uptime',
  'svn',
  'timestamp',
  'production',
  'meas_count_wifi',
  'capabilities',
  'capabilities.SUPPORT_SPI_MEMORY',
  'capabilities.SUPPORT_CHARGE_CONTROL',
  'capabilities.SUPPORT_IDLE_TIMER',
  'capabilities.SUPPORT_MDNS',
  'capabilities.SUPPORT_USB_LEGACY_MODE',
  'capabilities.WITH_CHARGE_LED',
  'capabilities.AP_PASS',
  'capabilities.CORE_DEBUG_LEVEL'
];
const expectedKeysNew = [
  'nuvoton_firmware',
  'nuvoton_chip_id',
  'serial',
  'sensor',
  'battery_voltage',
  'usb_voltage',
  'usb_charging',
];

@Injectable({
  providedIn: 'root'
})
export class UsbApiService {
  private oldSerialRegEx = '[45]W[0-9A-F]{18}';
  private isUSB = false;
  private firstConnection = false;

  constructor(
    private platform: Platform,
  ) {}

  async enableAP(state: StateInterface) {
    let serialDispatcher = new SerialDispatcher(state.CONNECTION_PARAMS, this.platform, state.SCANNER_VERSION, state.SCANNER_SERIAL);
    await serialDispatcher.write('get_status\n');
    const [status, res] = await serialDispatcher.getResult();
    serialDispatcher = null;
    try {
      const r = JSON.parse(res);
      return { status, wifi: r.host };
    } catch (e) {
      return { status: false, wifi: '' };
    }
  }

  async disableAP(state: StateInterface) {
    return;
  }

  async disableUSB(state: StateInterface) {
    if (!this.isUSB) { return; }
    let serialDispatcher = new SerialDispatcher(state.CONNECTION_PARAMS, this.platform, state.SCANNER_VERSION, state.SCANNER_SERIAL);
    await serialDispatcher.write('X\n');
    this.isUSB = false;
    serialDispatcher = null;
  }

  async enabledUSB(state: StateInterface, firstConnection = false, force = false) {
    if (this.isUSB && !false) { return; }
    let serialDispatcher = new SerialDispatcher(state.CONNECTION_PARAMS, this.platform, state.SCANNER_VERSION, state.SCANNER_SERIAL);
    if (!((state.SCANNER_SERIAL ?? '').startsWith('4W')) && !((state.SCANNER_SERIAL ?? '').startsWith('5W')) && !firstConnection) {
      await serialDispatcher.write('X\n');
      await serialDispatcher.write('start_usb\n');
    }
    this.isUSB = true;
    serialDispatcher = null;
  }

  /**
   * Performs hand recognition based on the given settings.
   * @param settings - The current settings for hand recognition.
   * @returns An Observable that emits a boolean value indicating if hand recognition was successful.
   */
  async doHandrecognitionObservable(state: StateInterface, settings: CurrentSettings, handOff = false): Promise<boolean> {
    await this.enabledUSB(state);
    let serialDispatcher = new SerialDispatcher(state.CONNECTION_PARAMS, this.platform, state.SCANNER_VERSION, state.SCANNER_SERIAL);
    // await new Promise((r => setTimeout(r, 50)));
    await serialDispatcher.write('W');
    const [status, res] = await serialDispatcher.getResult();
    serialDispatcher = null;
    if (!status) {
      return false;
    }
    try {
      const jsonParsed = JSON.parse(res);
      let result;
      const dark = jsonParsed[0];
      const bright = jsonParsed[1];
      if (bright < 0.0001) {
        return false;
      }
      if (state.SCANNER_SERIAL && state.SCANNER_SERIAL.match(this.oldSerialRegEx)) {
        result = (dark < 2) && ((bright > 500) && (bright < 6000));
      } else {
        const darkUpper = settings.handRecognitionLimitDarkUpper;
        const brightLower = settings.handRecognitionLimitBrightLower;
        const brightUpper = settings.handRecognitionLimitBrightUpper;
        result = (dark < darkUpper) && ((bright > brightLower) && (bright < brightUpper));
      }
      if (handOff) {
        result = !result;
      }
      return result;
    } catch (e) { return false; }
  }

  /**
   * Performs an AOX measurement.
   *
   * @returns An observable that emits HttpEvents during the measurement process.
   *                                      - The observable completes when the measurement is finished.
   * @param state state object
   * @param measurementType type of the usb measurement
   */
  doAOXMeasurementObservable(state: StateInterface, measurementType: string): Observable<HttpEvent<any>> {
    let totalLength = 254012;
    let command = 'M';
    switch (measurementType) {
      case 'full': {
        totalLength = 254012;
        command = 'M';
        break;
      }
      case 'front_half': {
        if (!state.SCANNER_VERSION || state.SCANNER_VERSION < 2) {
          break;
        }
        totalLength = 127028;
        command = 'K';
        break;
      }
      default: {
        totalLength = 254012;
        command = 'M';
        break;
      }
    }
    const initFunc = async () => {
      await this.enabledUSB(state);
      const serialDispatcherInit =
        new SerialDispatcher(state.CONNECTION_PARAMS, this.platform, state.SCANNER_VERSION, state.SCANNER_SERIAL);
      await serialDispatcherInit.write(command);
      return serialDispatcherInit;
    };

    let serialDispatcher: SerialDispatcher;
    let lastNum = 0;
    let lastTime = Date.now();
    let measurementDone = false;

    return from(initFunc()).pipe(
      switchMap(async _ => {
        serialDispatcher = _;
        return serialDispatcher.getProgress();
      }),
      switchMap(async prog => {
        const now = Date.now();
        if (prog > lastNum) {
          lastNum = prog;
          lastTime = now;
        }
        if ((now - lastTime) > 3000) {
          throw new TimeoutError();
        }
        if (prog < 100 || measurementDone) {
          return { type: HttpEventType.DownloadProgress, loaded: (totalLength / 100 * prog), total: totalLength } as HttpProgressEvent;
          // return ne w HttpResponse({body: prog, status: "running"});
        } else {
          measurementDone = true;
          const [status, result] = await serialDispatcher.getResult();
          if (status) {
            serialDispatcher = null;
            return new HttpResponse({ body: result });
          } else {
            return new HttpResponse({ body: '' });
          }
        }
      }),
      delay(10),
      timeout(1000),
      repeat(),
      catchError((err) => {
        return of(new HttpResponse({ body: '' }));
      })
    );
  }

  async getStatus(state: StateInterface): Promise<ScannerStatus> {

    const serial = state.SCANNER_SERIAL;
    if (!(['1E', '2E', '1N', '2N'].includes(serial.slice(0, 2)))) {
      return {
        battery_voltage: 4,
        host: 'biozoom_matrix',
        product: 'USB',
        ip: '',
        sensor: serial,
        sensor_firmware: '',
        serial,
        svn: '',
        timestamp: '',
        uptime: 100,
        wifi_firmware: '',
        wifi_name: '',
        wifi_strength: 80
      };
    }

    let serialDispatcher = new SerialDispatcher(state.CONNECTION_PARAMS, this.platform, state.SCANNER_VERSION, state.SCANNER_SERIAL);
    await this.disableUSB(state);
    // await serialDispatcher.write('X\n');
    // await new Promise((r => setTimeout(r, 10)));
    await serialDispatcher.write('get_status\n');
    const [status, res] = await serialDispatcher.getResult();
    if (res.includes('\r\n')) {
      try {
        const jsonString = res.replace('\r\n', '');
        const json = JSON.parse(jsonString);
        const keys = [];
        for (const key of Object.keys(json)) {
          keys.push(key);
        }
        if (keys.includes('capabilities')) {
          for (const key of Object.keys(json.capabilities)) {
            keys.push(`capabilities.${key}`);
          }
        }
        for (const key of ((state.SCANNER_SERIAL ?? '').startsWith('1N') ? expectedKeysNew : expectedKeys)) {
          if (!(keys.includes(key))) {
            serialDispatcher = null;
            return;
          }
        }
        // await serialDispatcher.write('start_usb\n');
        serialDispatcher = null;
        return json;
      } catch (e) {
        serialDispatcher = null;
        return;
      }
    }
    serialDispatcher = null;
    return;
  }

  async disconnectScanner(state: StateInterface) {
    if (this.platform.is('cordova') && this.platform.is('android')) {
      try {
        // await this.serial.close();
        console.log('Scanner disconnected');
      } catch (e) {
        console.log('Scanner could not be disconnected');
        // pass
      }
    }
  }

  async deleteFlash(state: StateInterface) {
    await this.disableUSB(state);
    let serialDispatcher = new SerialDispatcher(state.CONNECTION_PARAMS, this.platform, state.SCANNER_VERSION, state.SCANNER_SERIAL);
    await serialDispatcher.write('flash_erase\n');
    const [status, res] = await serialDispatcher.getResult();
    serialDispatcher = null;
    return res.includes('\r\n') && !res.includes('task_wdt:');
  }

  async checkPermissionAndOpenSerialConnection(
    state: StateInterface,
    setState: ((key: State, val: any) => void),
    setBlockScreen: ((block: boolean) => void) = (() => { })
  ): Promise<[string, number]> {
    let params = [];
    const prevParams = state?.CONNECTION_PARAMS;
    if (prevParams && prevParams !== '') {
      params.push(prevParams);
    }
    for (let i = 0; i < 1; i++) {
      params.push({ baudRate: 2000000 });
      params.push({ baudRate: 500000 });
    }

    params = params.filter((v, i, a) =>
      i === a.findIndex((t) => t.vid === v.vid && t.pid === v.pid && t.driver === v.driver && t.baudRate === v.baudRate)
    );
    let _;

    if (state.CONNECTION_PARAMS && state.CONNECTION_PARAMS !== '' && this.firstConnection) {
      let serialDispatcherValidate = new SerialDispatcher(state.CONNECTION_PARAMS, this.platform, state.SCANNER_VERSION, state.SCANNER_SERIAL);
      await this.enabledUSB(state);
      // await serialDispatcherValidate.write('X\n');
      await this.disableUSB(state);
      _ = await serialDispatcherValidate.getResult();
      const isMatrix = (state?.SCANNER_SERIAL ?? '').startsWith('4W') || (state?.SCANNER_SERIAL ?? '').startsWith('5W');
      const isUSB = (state?.SCANNER_SERIAL ?? '').startsWith('1N') || (state?.SCANNER_SERIAL ?? '').startsWith('2N');
      if (isMatrix) {
        await serialDispatcherValidate.write('I');
      } else if (isUSB) {
        await serialDispatcherValidate.write('status\n');
      } else {
        await serialDispatcherValidate.write('get_status\n');
      }

      const [status, result] = await serialDispatcherValidate.getResult();
      if (status) {
        if (!isMatrix) {
          await this.enabledUSB(state);
        }
        if (isMatrix) {
          serialDispatcherValidate = null;
          const [serialNumReset, versionNumReset] = result.split('-');
          return [serialNumReset, Number(versionNumReset)];
        } else {
          let [resParsed, err] = parseStatus(result, setState);
          let i = 0;
          while (resParsed[0] === '' && i < 5) {
            await serialDispatcherValidate.write('get_status\n');
            const [statusRetry, resultRetry] = await serialDispatcherValidate.getResult();
            if (statusRetry) {
              [resParsed, err] = parseStatus(resultRetry, setState);
            }
            i++;
          }
          if (err) {
            await serialDispatcherValidate.write('status\n');
            const [statusRetry, resultRetry] = await serialDispatcherValidate.getResult();
            if (statusRetry) {
              parseStatus(resultRetry, setState);
            }
          }
          serialDispatcherValidate = null;
          return resParsed;
        }
      }
      await serialDispatcherValidate.close();
      serialDispatcherValidate = null;
    }
    this.firstConnection = false;

    for (const param of params) {
      let serialDispatcherPermission = new SerialDispatcher(param, this.platform, 1, state.SCANNER_SERIAL);
      await serialDispatcherPermission.requestPermission();
      const [status, result] = await serialDispatcherPermission.getResult();
      if (!status) {
        serialDispatcherPermission = null;
        if (result === 'No device connected.') {
          break;
        }
      } else {
        setBlockScreen(true);
        await serialDispatcherPermission.open();
        _ = await serialDispatcherPermission.getResult();
        serialDispatcherPermission = null;
        let serialDispatcherIdent = new SerialDispatcher(param, this.platform, 1, state.SCANNER_SERIAL);
        this.isUSB = false;
        await this.enabledUSB(state, true);
        await this.disableUSB(state);
        _ = await serialDispatcherIdent.getResult();
        // await serialDispatcherIdent.write('X\n');
        await serialDispatcherIdent.write('get_status\n');
        const [statusIdent, resIdent] = await serialDispatcherIdent.getResult();
        if (!statusIdent) {
          await this.enabledUSB(state, false, true);
          await this.disableUSB(state);
          _ = await serialDispatcherIdent.getResult();


          await serialDispatcherIdent.write('get_status\n');
          const [statusIdentReset1, resIdentReset1] = await serialDispatcherIdent.getResult();
          if (statusIdentReset1) {
            await this.enabledUSB(state);
            let [res1, err] = parseStatus(resIdentReset1, setState);
            if (err) {
              await serialDispatcherIdent.write('status\n');
              const [statusRetry, resultRetry] = await serialDispatcherIdent.getResult();
              if (statusRetry) {
                parseStatus(resultRetry, setState);
              }
            }
            serialDispatcherIdent = null;
            setState(State.CONNECTION_PARAMS, param);
            this.firstConnection = true;
            setBlockScreen(false);
            return [res1[0], Number(res1[1])];
          }

          // await serialDispatcherIdent.write('X\n');
          await serialDispatcherIdent.write('I');
          const [statusIdentReset2, resIdentReset2] = await serialDispatcherIdent.getResult();
          if (statusIdentReset2) {
            serialDispatcherIdent = null;
            const [serialNumReset2, versionNumReset2] = resIdentReset2.split('-');
            setState(State.CONNECTION_PARAMS, param);
            this.firstConnection = true;
            setBlockScreen(false);
            return [serialNumReset2, Number(versionNumReset2)];
          }
          await serialDispatcherIdent.close();
        } else {
          await this.enabledUSB(state);
          let [res1, err] = parseStatus(resIdent, setState);
          if (err) {
            await serialDispatcherIdent.write('status\n');
            const [statusRetry, resultRetry] = await serialDispatcherIdent.getResult();
            if (statusRetry) {
              parseStatus(resultRetry, setState);
            }
          }
          serialDispatcherIdent = null;
          setState(State.CONNECTION_PARAMS, param);
          this.firstConnection = true;
          setBlockScreen(false);
          return [res1[0], Number(res1[1])];
        }
      }
    }
    setBlockScreen(false);
    return ['', 0];
  }
}
